import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface DemoPosition {
  id: string;
  symbol: string;
  side: string;
  quantity: number;
  average_entry_price: number;
  unrealized_pnl: number;
  realized_pnl: number;
  status: string;
  opened_at: string;
  strategy_instance_id: string;
  currentPrice?: number;
}

export function useDemoAccountPositions(accountId: string) {
  const { user } = useAuth();
  const [positions, setPositions] = useState<DemoPosition[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPositions = useCallback(async () => {
    if (!user?.id || !accountId) {
      setPositions([]);
      setLoading(false);
      return;
    }

    // Find strategy_instance_ids linked to this demo account
    const { data: params } = await supabase
      .from('strategy_parameters')
      .select('strategy_instance_id')
      .eq('param_name', 'demo_account_id')
      .eq('param_value', accountId);

    const instanceIds = params?.map((p) => p.strategy_instance_id) ?? [];

    if (instanceIds.length === 0) {
      setPositions([]);
      setLoading(false);
      return;
    }

    const { data: posData } = await supabase
      .from('strategy_positions')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'open')
      .in('strategy_instance_id', instanceIds);

    // Fetch current market prices for position symbols
    const symbols = [...new Set((posData ?? []).map((p) => p.symbol))];
    let priceMap: Record<string, number> = {};

    if (symbols.length > 0) {
      const { data: prices } = await supabase
        .from('market_prices')
        .select('symbol, price')
        .in('symbol', symbols);

      if (prices) {
        priceMap = Object.fromEntries(prices.map((p) => [p.symbol, Number(p.price)]));
      }
    }

    const mapped: DemoPosition[] = (posData ?? []).map((p) => ({
      ...p,
      quantity: Number(p.quantity),
      average_entry_price: Number(p.average_entry_price),
      unrealized_pnl: Number(p.unrealized_pnl),
      realized_pnl: Number(p.realized_pnl),
      currentPrice: priceMap[p.symbol],
    }));

    setPositions(mapped);
    setLoading(false);
  }, [user?.id, accountId]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  // Realtime subscription
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`positions-${accountId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'strategy_positions' }, () => fetchPositions())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'market_prices' }, () => fetchPositions())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, accountId, fetchPositions]);

  return { positions, loading, refetch: fetchPositions };
}
